import * as React from 'react';
import { FieldTitle, useResourceContext } from 'ra-core';
import { Switch, Typography } from '@mui/material';
import DragIcon from '@/assets/icons/drag-icon.svg?react';
import { styled } from '@mui/material/styles';
import { Palette } from '@/themes/themeOverrides/Palette';

export const CustomFieldToggle = ({ selected, label, onToggle, onMove, source, index }) => {
    const resource = useResourceContext();
    const dropIndex = React.useRef(null);
    const x = React.useRef(null);
    const y = React.useRef(null);

    const handleDocumentDragOver = React.useCallback(event => {
        x.current = event.clientX;
        y.current = event.clientY;
    }, []);

    const handleDragStart = () => {
        document.addEventListener('dragover', handleDocumentDragOver);
    };

    const handleDrag = event => {
        const selectedItem = event.target;
        selectedItem.classList.add('drag-active');
        const list = selectedItem.closest('ul');
        let dropItem = document.elementFromPoint(x.current, y.current) === null ? selectedItem : document.elementFromPoint(x.current, y.current).closest('li');

        if (!dropItem) {
            return;
        }
        if (dropItem.classList.contains('dragIcon')) {
            dropItem = dropItem.parentNode;
        }
        if (dropItem === selectedItem) {
            return;
        }
        if (list === dropItem.parentNode.closest('ul')) {
            dropIndex.current = dropItem.dataset.index;
            if (dropItem === selectedItem.nextSibling) {
                dropItem = dropItem.nextSibling;
            }
            if (dropItem) {
                dropItem.before(selectedItem);
            } else {
                list.append(selectedItem);
            }
        }
    };

    const handleDragEnd = event => {
        const selectedItem = event.target;
        const list = selectedItem.closest('ul');
        let dropItem = document.elementFromPoint(x.current, y.current) === null ? selectedItem : document.elementFromPoint(x.current, y.current).closest('li');

        if (!dropItem) {
            if (y.current > selectedItem.closest('ul').getBoundingClientRect().bottom) {
                dropItem = list.lastChild;
            } else {
                dropItem = list.firstChild;
            }
        }

        if (dropItem && list === dropItem.closest('ul')) {
            onMove(selectedItem.dataset.index, dropIndex.current);
        } else {
            event.preventDefault();
            event.stopPropagation();
        }
        selectedItem.classList.remove('drag-active');
        document.removeEventListener('dragover', handleDocumentDragOver);
    };

    const handleDragOver = event => {
        event.preventDefault();
        event.dataTransfer.dropEffect = 'move';
    };

    return (
        <Root
            key={source}
            draggable={onMove ? 'true' : undefined}
            onDrag={onMove ? handleDrag : undefined}
            onDragStart={onMove ? handleDragStart : undefined}
            onDragEnd={onMove ? handleDragEnd : undefined}
            onDragOver={onMove ? handleDragOver : undefined}
            data-index={index}
        >
            <label htmlFor={`switch_${index}`}>
                <Switch checked={selected} onChange={onToggle} name={index} id={`switch_${index}`} size='small' sx={{ mr: 0.5, ml: -0.5 }} />
                <Typography className='field-title' variant='body2' component='span'>
                    <FieldTitle label={label} source={source} resource={resource} />
                </Typography>
            </label>
            {onMove && <DragIcon className='dragIcon' />}
        </Root>
    );
};

const Root = styled('li', {
    name: 'RaFieldToggle',
    overridesResolver: (_props, styles) => styles.root,
})(() => ({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '9px 8px',
    cursor: 'pointer',
    borderRadius: '8px',
    '&:hover': {
        backgroundColor: Palette.yellow.yellow50,
    },
    label: {
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        cursor: 'pointer',
        paddingRight: '14px',
    },
    '& .field-title': {
        fontSize: '14px',
    },
    '& svg': {
        cursor: 'move',
    },
    '&.drag-active': {
        background: 'transparent',
        color: 'transparent',
        outline: '1px solid rgba(0, 0, 0, 0.08)',
        '& .MuiSwitch-root, & svg': {
            visibility: 'hidden',
        },
    },
    '& .MuiSwitch-root': {
        height: 'auto',
        width: 'auto',
        padding: '0',
        margin: '0',
        '& .MuiSwitch-switchBase': {
            padding: '5px 4px',
            borderRadius: '4px',
        },
        '& .MuiSwitch-track': {
            width: '28px',
            height: '18px',
        },
    },
    '& .MuiButtonBase-root.MuiSwitch-switchBase': {
        '& .MuiSwitch-thumb': {
            width: '12px',
            height: '12px',
            backgroundColor: Palette.gray.gray800,
            outline: `2px solid ${Palette.gray.gray900}`,
            outlineOffset: '-2px',
        },
    },
    '& .MuiButtonBase-root.MuiSwitch-switchBase.Mui-checked': {
        left: '-4px',
    },
}));
